:global([role="dialog"]) .container {
  display: contents;
}

.tabs {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.tabsListWrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid var(--border-subtle);
}

.tabsListWrapper.hideBorder {
  border-bottom: none;
}

.tabsListWrapper.canScrollLeft::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: var(--size-input-height-md);
  z-index: 1;
  background: linear-gradient(to right, var(--surface-default) 0%, var(--surface-default) 60%, transparent 100%);
}

.tabsListWrapper.canScrollRight::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: var(--size-input-height-md);
  z-index: 1;
  background: linear-gradient(to left, var(--surface-default) 0%, var(--surface-default) 60%, transparent 100%);
}

.tabsList {
  display: flex;
  flex: 1 0 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: var(--spacing-3xl);
  padding: 0 var(--spacing-xl);
}

/* Hide scrollbar for Chrome, Safari and Opera */
.tabsList::-webkit-scrollbar {
  display: none;
}

.tabsTrigger {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--content-primary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  /* This provides a bit of extra space so that the focus outline is not clipped. */
  padding: 0 var(--spacing-xs);
  margin: 0 calc(var(--spacing-xs) * -1);

  @media (width <= 450px) {
    flex-grow: 1;
  }
}

/* Stack the visible label and the invisible ghost in the same grid cell.
   The ghost is always rendered at the active/hover weight, reserving the
   widest intrinsic width so the row doesn't shift when the live label
   swaps weights on hover/active. */
.tabsTriggerLabel,
.tabsTriggerGhost {
  grid-area: 1 / 1;
}

.tabsTriggerGhost {
  visibility: hidden;
  pointer-events: none;
  user-select: none;
}

/* Force the ghost (and anything it wraps — e.g. <AT>, <DDSLabelText>)
   to render at the active/hover weight. The descendant selector matters
   because typography wrappers set their own `font:` shorthand on inner
   elements, which would otherwise override the ghost styling and let
   the width reservation silently break for those consumers.
   `line-height: 1` must follow `font:` (which bakes in the token's
   snug line-height) so the ghost line-box matches the visible label
   and the row height doesn't grow. `letter-spacing` isn't part of the
   `font:` shorthand, so it's set separately.
   Invariant: --label-{md,lg}-font must render at least as wide as the
   resting --body-{md,lg}-heavy-font with the same glyphs. If a future
   token change inverts that (label narrower than body), the ghost
   under-reserves and the layout shift this fix exists to prevent
   silently returns. */
.tabsTrigger.medium .tabsTriggerGhost,
.tabsTrigger.medium .tabsTriggerGhost * {
  font: var(--label-md-font);
  letter-spacing: var(--label-md-letter-spacing);
  line-height: 1;
}

.tabsTrigger.large .tabsTriggerGhost,
.tabsTrigger.large .tabsTriggerGhost * {
  font: var(--label-lg-font);
  letter-spacing: var(--label-lg-letter-spacing);
  line-height: 1;
}

.tabsTrigger:focus-visible {
  outline-offset: calc(var(--spacing-xs) * -1);
  outline: var(--spacing-xs) solid var(--content-accent);
}

.tabsTrigger[disabled] {
  color: var(--content-on-action-disabled);
  cursor: not-allowed;
}

.tabsTrigger:hover:not([disabled]) {
  color: var(--content-primary);
}

.tabsTrigger[data-state="active"] {
  color: var(--content-navigation-active);
}

.tabsTrigger[data-state="active"]::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--spacing-xs);
  border-radius: var(--radius-xs);
  background-color: var(--border-action-input-focus);
}

.tabsTrigger.medium {
  height: var(--size-input-height-sm);
  font: var(--body-md-heavy-font);
  font-weight: var(--body-md-heavy-font-weight);
  letter-spacing: var(--body-md-heavy-letter-spacing);
  line-height: 1;
}

.tabsTrigger.medium:hover:not([disabled]),
.tabsTrigger.medium[data-state="active"] {
  font: var(--label-md-font);
  font-weight: var(--label-md-font-weight);
  letter-spacing: var(--label-md-letter-spacing);
}

.tabsTrigger.large {
  height: var(--size-input-height-md);
  font: var(--body-lg-heavy-font);
  font-weight: var(--body-lg-heavy-font-weight);
  letter-spacing: var(--body-lg-heavy-letter-spacing);
  line-height: 1;
}

.tabsTrigger.large[data-state="active"],
.tabsTrigger.large:hover:not([disabled]) {
  font: var(--label-lg-font);
  font-weight: var(--label-lg-font-weight);
  letter-spacing: var(--label-lg-letter-spacing);
}

/* Wrapper handles absolute positioning + vertical centering so the
   inner button is free to apply its own hover transform without losing
   the centering. (DDSIconButton's secondary hover sets `transform`,
   which would otherwise clobber a translateY(-50%) on the button.) */
.scrollButtonSlot {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: var(--z-index-layer-raised);
  display: flex;
  align-items: center;
  pointer-events: none;
}

.scrollButtonSlotLeft {
  left: var(--spacing-xs);
}

.scrollButtonSlotRight {
  right: var(--spacing-xs);
}

.scrollButton {
  pointer-events: auto;
  box-shadow: var(--shadow-raised);
}

.tabsContent {
  padding: var(--spacing-md) var(--spacing-sm);
}
